import { type FC } from 'react'

import { useAutomatedMessageContext } from '@liveHelper/components/AutomatedMessageProvider'
import { IdlePopupNotification } from '@liveHelper/components/Idle/IdlePopupNotification'
import { MessagePreview } from '@liveHelper/components/MessagePreview'
import { AutomatedMessagePreview } from '@liveHelper/components/MessagePreview/AutomatedMessagePreview'
import { useMessagePreviewContext } from '@liveHelper/components/MessagePreviewProvider'
import { MessagePreviewWrapper } from '@liveHelper/components/shared/WidgetContainer/WidgetContainer.styles'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useIsOffline } from '@liveHelper/hooks/useIsOffline'
import { useWidgetPosition } from '@liveHelper/hooks/useWidgetPosition'
import {
  isIdleStateTextEligibleToShowSelector,
  paramsSelector,
  queuePositionSelector,
  uiStateSelector,
  useStore,
} from '@liveHelper/models/store'
import { MESSAGE_PREVIEW_WRAPPER } from '@liveHelper/testIds'

interface WidgetMessagePreviewProps {
  modalHeight: undefined | number
}

const listOfValidUIStates = [
  'idle_minimized',
  'on_call_minimized',
  'dialing_minimized',
]

/**
 * A place for message preview to anchor on top of the main widget
 */
export const WidgetMessagePreview: FC<WidgetMessagePreviewProps> = ({
  modalHeight,
}) => {
  const { idle_minimized_z_index } = useStore(paramsSelector)
  const { showWidgetOnLeftSide } = useWidgetPosition()

  const uiState = useStore(uiStateSelector)
  const queuePosition = useStore(queuePositionSelector)

  const chatChannelConfig = useChatChannelConfig()
  const { lastUnreadMessage } = useMessagePreviewContext()
  const { automatedMessage } = useAutomatedMessageContext()
  const isOffline = useIsOffline()
  const isIdleTextEligible = useStore(isIdleStateTextEligibleToShowSelector)

  const idleStateText = chatChannelConfig?.widget_style?.idle_state_text
  const isAvatarIdleUI = Boolean(
    chatChannelConfig?.widget_style?.icon_style === 'avatar',
  )
  const zIndex =
    uiState === 'idle_minimized' && idle_minimized_z_index
      ? idle_minimized_z_index
      : undefined

  if (
    (lastUnreadMessage ||
      automatedMessage?.src === 'chat_channel' ||
      (idleStateText && isIdleTextEligible)) &&
    listOfValidUIStates.includes(uiState)
  ) {
    return (
      <MessagePreviewWrapper
        modalHeight={modalHeight}
        moreThanOneMessage={
          lastUnreadMessage
            ? lastUnreadMessage.numberOfUnreadMessages > 1
            : false
        }
        isPositionedLeft={showWidgetOnLeftSide}
        data-testid={MESSAGE_PREVIEW_WRAPPER}
        zIndex={zIndex}
      >
        {lastUnreadMessage ? (
          <MessagePreview
            content={lastUnreadMessage.message}
            attachment={lastUnreadMessage.attachment}
            number={lastUnreadMessage.numberOfUnreadMessages}
          />
        ) : automatedMessage ? (
          <AutomatedMessagePreview />
        ) : idleStateText &&
          isAvatarIdleUI &&
          uiState === 'idle_minimized' &&
          !isOffline &&
          !queuePosition ? (
          <IdlePopupNotification />
        ) : null}
      </MessagePreviewWrapper>
    )
  }

  return null
}
